import { Link } from "react-router-dom";
import PageMeta from "@/components/seo/PageMeta";
import { articleSchema, breadcrumbSchema, faqSchema } from "@/components/seo/schema";
import { CONTENT_DATE, CONTENT_DATE_ISO, SITE_URL, TRACKER_OG } from "@/data/site";

export function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
      {children}
    </a>
  );
}

export function DataTable({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="overflow-x-auto terminal-card my-4">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border/40">
            {headers.map(header => (
              <th key={header} className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/20 align-top">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 text-foreground/90">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ArticleShellProps {
  title: string;
  description: string;
  path: string;
  kicker: string;
  children: React.ReactNode;
  faqs?: { question: string; answer: string }[];
  crumbs?: { name: string; path: string }[];
  /** Date this page was last source-checked; defaults to the site-wide content date. */
  lastChecked?: string;
  lastCheckedIso?: string;
  /** Optional visible H1 (e.g. A/B variant); meta title and JSON-LD keep `title`. */
  heading?: string;
  /** Optional exact <title>/og:title override; body copy and JSON-LD keep `title`. */
  metaTitle?: string;
}

export default function ArticleShell({ title, description, path, kicker, children, faqs = [], crumbs, lastChecked = CONTENT_DATE, lastCheckedIso = CONTENT_DATE_ISO, heading, metaTitle }: ArticleShellProps) {
  const trail = crumbs ?? [
    { name: "Home", path: "/" },
    { name: title, path },
  ];
  const jsonLd = [
    articleSchema(title, description, path, lastCheckedIso, TRACKER_OG),
    breadcrumbSchema(trail),
    ...(faqs.length ? [faqSchema(faqs)] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title={metaTitle ?? `${title} | PeptideX`}
        description={description}
        path={path}
        dateModified={lastCheckedIso}
        ogType="article"
        ogImage={TRACKER_OG}
        jsonLd={jsonLd}
      />
      <article className="container mx-auto px-4 sm:px-6 pt-24 pb-8 max-w-3xl">
        <nav className="flex flex-wrap items-center gap-2 text-[11px] font-mono text-muted-foreground mb-6">
          <Link to="/" className="hover:text-primary">Home</Link>
          {trail.slice(1, -1).map(crumb => (
            <span key={crumb.path} className="flex items-center gap-2">
              <span>/</span>
              <Link to={crumb.path} className="hover:text-primary">{crumb.name}</Link>
            </span>
          ))}
          <span>/</span>
          <span className="text-foreground">{trail[trail.length - 1]?.name}</span>
        </nav>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary mb-3">{kicker}</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">{heading ?? title}</h1>
        <p className="font-mono text-[11px] text-muted-foreground mb-8">
          Last checked: {lastChecked}. Educational only, not medical advice. PeptideX is not affiliated with any drug manufacturer.
        </p>
        <div className="space-y-6 text-sm leading-relaxed" style={{ color: "hsl(215 25% 22%)" }}>
          {children}
        </div>
        <p className="font-mono text-[10px] text-muted-foreground mt-10">
          Canonical: {SITE_URL}{path}
        </p>
      </article>
    </div>
  );
}
